import { BLUE_RIBBON_INACTIVE_REASONS, PartyJackpotType } from './../../../../../common/interfaces/blue-ribbon-party-jackpot.interface';
import { IAnalyticsData } from './../../analytics/analytics_service_interface';
import {BlueRibbonJackpot} from './blue_ribbon_jackpot';
import {
    IBlueRibbonPartyJackpot,
    PartyJackpotJoinStatus
} from '../../../../../common/interfaces/blue-ribbon-party-jackpot.interface';
import { TimeSpan, Check } from '@sparkware/app-utils';
import ServiceFactory from '../../service_factory';
import Logger from '../../../utils/logger';
import { observable, action } from 'mobx';
import { JACKPOT_STATE } from '../../../../../common/const/jackpot_map_ids';

const INVALID_DATE = new Date(0);

export class BlueRibbonPartyJackpot extends BlueRibbonJackpot implements IBlueRibbonPartyJackpot {
    constructor(private readonly serviceFactory: ServiceFactory,
                private readonly logger: Logger,
                jackpotId: string,
                private readonly _games: string[]
                ) {
        super(jackpotId, serviceFactory);
    }

    public hasGame(gameType: number): boolean {
        return this._games.includes(gameType.toString());
    }

    public getStandardJackpotState(): JACKPOT_STATE {
        if (this._isInOffHours()) {
            return JACKPOT_STATE.OffHours;
        }

        // return COLD status if JP is not active
        if (!this.isActive) {
            return JACKPOT_STATE.Cold;
        }

        // status is active below
        // if next drop will be tomorrow it means we are off hours
        if (this._nextDropWillBeTomorrow()) {
            return JACKPOT_STATE.OffHours;
        }

        switch (this.potState) {
            case "COLD":
            case "CHILLY":
                // even if status is cold we avoid returning it since widget component thinks it was won already and hides timer
                return JACKPOT_STATE.Regular;
            case "WARM":
            case "HOT":
                return JACKPOT_STATE.Hot;
            default:
                return JACKPOT_STATE.Regular;
        }
    }

    @observable.ref
    private _type: PartyJackpotType = PartyJackpotType.Daily;

    public get type(): PartyJackpotType {
        return this._type;
    }

    @action.bound
    public setType(value: string): void {
        try {
            const type = value.trim().toUpperCase();
            switch(type) {
                case "HOURLY":
                    this._type = PartyJackpotType.Hourly;
                    break;
                case "DAILY":
                    this._type = PartyJackpotType.Daily;
                    break;

                default:
                    this._type = PartyJackpotType.Daily;
                    this.logger.warn(`Invalid jpType received: ${value}, defaulting to daily`);
            }
        } catch(err) {
            this.logger.warn(`Invalid jpType received: ${value} for jackpotID ${this.jackpotId}`, err);
        }
    }


    @observable.ref
    private _lastWin: number = null;

    get lastWin() {
        return this._lastWin;
    }

    @action.bound
    setLastWin(lastWin: number | null) {
        if (Check.isNumber(lastWin)) {
            const now = this._now();
            const delta = Number(now) - lastWin;
            if (delta < 0) {
                // last win is in the future?
                // yes, it can happen unfortunately because our _mow() function is not so accurate
                // set it anyway, but log this info to have evidence
                const secondsDifference = Math.floor(TimeSpan.fromMilliseconds(-delta).totalSeconds);
                const msg = `Invalid lastWin received (greater than current time)! LastWin: (${lastWin}) Now: ${now}: DifferenceInSeconds: ${secondsDifference}`;
                if (secondsDifference >= 10) {
                    this.logger.error(msg);
                } else {
                    this.logger.warn(msg);
                }
            }
        }

        if (this._lastWin !== lastWin) {
            this.logger.info(`LastWin updated from ${this._lastWin} to ${lastWin} for jackpotID ${this.jackpotId}`);
        }
        this._lastWin = lastWin;
    }

    @observable.ref
    private _hasRealMoneyPrize: boolean = true;

    get hasRealMoneyWinPrize() {
        return this._hasRealMoneyPrize;
    }

    @action.bound
    setHasRealMoneyWinPrize(isRealPrizeCustomField: string) {
        try {
            if (!Check.isNullOrUndefined(isRealPrizeCustomField)) {
                this._hasRealMoneyPrize = isRealPrizeCustomField?.toString().toLocaleLowerCase() === "true";
                this.logger.info(`isRealPrize received as ${this._hasRealMoneyPrize} for jackpotID ${this.jackpotId}`);
            } else {
                this._hasRealMoneyPrize = true;
                this.logger.warn(`isRealPrize not specified for jackpotID ${this.jackpotId}, defaulting to real money prize`);
            }
        } catch(err) {
            this.logger.warn(`Invalid isRealPrize received: ${isRealPrizeCustomField} for jackpotID ${this.jackpotId}`, err);
        }
    }

    @observable.ref
    private _mustDropHour: TimeSpan = TimeSpan.fromSeconds(0);

    @action.bound
    setMustDropHour(mustDropValue: string) {
        try {
            this._mustDropHour = TimeSpan.parse(mustDropValue);
            this.logger.info(`mustDropValue ${mustDropValue} set for jackpotID ${this.jackpotId}`);
        } catch(err) {
            this.logger.warn(`Invalid mustDropValue received ${mustDropValue} for jackpotID ${this.jackpotId}`, err);
        }
    }

    @observable.ref
    private _startHour: TimeSpan = TimeSpan.fromSeconds(0);

    get startHour(): string {
        return BlueRibbonPartyJackpot._getFormattedLocalTime(this._startHour);
    }

    private static _getFormattedLocalTime(utcTime: TimeSpan) {
        const localDate = utcTime.toLocalTime();
        let result = (localDate.hours >= 12)? `${localDate.hours === 12? localDate.hours : localDate.hours - 12}PM` : `${localDate.hours}AM`;
        if (localDate.minutes > 0) {
            result += `:${localDate.minutes}`;
        }
        return result;
    }

    @action.bound
    setStartHour(startHourValue: string) {
        try {
            this._startHour = TimeSpan.parse(startHourValue);
            this.logger.info(`startHour ${startHourValue} set for jackpotID ${this.jackpotId}`);
        } catch(err) {
            this.logger.warn(`Invalid startHour received ${startHourValue} for jackpotID ${this.jackpotId}`, err);
        }
    }

    @observable.ref
    private _endHour: TimeSpan = TimeSpan.fromSeconds(0);

    get endHour(): string {
        return BlueRibbonPartyJackpot._getFormattedLocalTime(this._endHour);
    }

    @action.bound
    setEndHour(endHourValue: string) {
        try {
            this._endHour = TimeSpan.parse(endHourValue);
            this.logger.info(`endHour ${endHourValue} set for jackpotID ${this.jackpotId}`);
        } catch(err) {
            this.logger.warn(`Invalid endHour received ${endHourValue} for jackpotID ${this.jackpotId}`, err);
        }
    }

    @observable.ref
    private _b2cLink: string;

    get B2CLink() {
        return this._b2cLink;
    }

    @action.bound
    setB2CLink(B2CLinkUrl: string) {
        this._b2cLink = B2CLinkUrl;
    }

    @observable.ref
    private _liveCasino: string = '';

    @action.bound
    setLiveCasino(liveCasinoValue: string) {
        try {
            this._liveCasino = liveCasinoValue.toString().toUpperCase();
            this.logger.info(`liveCasino ${liveCasinoValue} set for jackpotID ${this.jackpotId}`);
        } catch(err) {
            this.logger.warn(`Invalid liveCasinoValue received ${liveCasinoValue} for jackpotID ${this.jackpotId}`, err);
        }
    }

    get supportsAllLiveGames(): boolean {
        return this._liveCasino === "ALL";
    }

    // in all computations (for example in _getDailyMustHitDate() and _getHourlyMustHitDate())
    // we use UTC functions because JackpotWidgetComponent do this 
    // mustHitTime.getTime() - serverTime
    // to compute time left 
    // and both getTime and serverTime returns UTC values
    get mustHitTime(): Date {
        if (this.isExpired) {
            return INVALID_DATE;
        }

        if (this.type === PartyJackpotType.Daily) {
            if (!this._mustDropHour.totalSeconds) {
                return INVALID_DATE;
            }
    
            return this._getDailyMustHitDate();
        }

        if (this.type === PartyJackpotType.Hourly) {
            if (!this._startHour.totalSeconds || !this._endHour.totalSeconds) {
                return INVALID_DATE;
            }
    
            return this._getHourlyMustHitDate();
        }

        return INVALID_DATE;
    }

    private _getDailyMustHitDate(): Date {
        let todayMustHitDate = this._getTodayMustHitDate();
        if (Check.isNullOrUndefined(this._lastWin)) {
            return todayMustHitDate;
        }

        if (!this._jackpotWillDropToday()) {
            // make it day++ (tomorrow)
            todayMustHitDate.setUTCDate(todayMustHitDate.getUTCDate() + 1);
        }

        return todayMustHitDate;
    }

    private _now(): Date {
        let now = new Date(this.serviceFactory.clockService().getCurrentServerDate());
        if (Number(now) === 0) {
            now = new Date(this.serviceFactory.timeService().now()); 
            this.logger.warn(`for jackpotID ${this.jackpotId}, clockService().getCurrentServerDate() returned Date(0), defaulting to timeService().now(): ${now.toString()}`);
        }
        return now;
    }

    private _getTodayMustHitDate(): Date {
        const now = this._now();
        const todayMustHit = new Date(0);
        todayMustHit.setUTCFullYear(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
        todayMustHit.setUTCHours(this._mustDropHour.hours, this._mustDropHour.minutes, this._mustDropHour.seconds, 0);
        return todayMustHit;
    }

    private _getHourlyMustHitDate(): Date {
        const now = this._now();
        const nowStartHour = this._getHourlyNextHour(now, 0);
        const nowEndHour = this._getHourlyNextHour(now, 1);
        let nextComputedDrop = nowEndHour;

        if (this._lastWin) {
            const lastWinDate = new Date(this._lastWin);
            // if lastWin is in-between current hour
            if (lastWinDate >= nowStartHour && lastWinDate < nowEndHour) {
                nextComputedDrop = this._getHourlyNextHour(now, 2);
            }
        }
        const tomorrowAtStartDate = this._getHourlyStartDate(1);

        // if date is changed, this means we reached with nextDrop next day
        if (nextComputedDrop.getUTCDate() !== now.getUTCDate()) {
            // we arrived in next day, it should be tomorrow at start date
            return tomorrowAtStartDate;
        }

        // this means that we are before operating hours
        const startDateToday = this._getHourlyStartDate(0);
        if (nextComputedDrop < startDateToday) {
            return startDateToday;
        }

        // inside operating hours
        const endDateToday = this._getHourlyEndDate();
        if (nextComputedDrop >= startDateToday && nextComputedDrop <= endDateToday) {
            return nextComputedDrop;
        }

        // after operating hours
        return tomorrowAtStartDate;
    }

    private _nextDropWillBeTomorrow() {
        if (!this.isActive || !this._lastWin) {
            return false;
        }

        const tomorrowAtStartDate = this._getHourlyStartDate(1);
        const nextDrop = this._getHourlyMustHitDate();
        return nextDrop.getTime() === tomorrowAtStartDate.getTime();
    }

    private _getHourlyNextHour(source: Date, hourOffset: number): Date {
        const nextDrop = new Date(0);
        nextDrop.setUTCFullYear(source.getUTCFullYear(), source.getUTCMonth(), source.getUTCDate());
        nextDrop.setUTCHours(source.getUTCHours() + hourOffset, 0, 0, 0);
        return nextDrop;
    }

    private _getHourlyStartDate(daysToAdd: number): Date {
        const now = this._now();
        const startDate = new Date(0);
        startDate.setUTCFullYear(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysToAdd);
        startDate.setUTCHours(this._startHour.hours, this._startHour.minutes, this._startHour.seconds, 0);
        return startDate;
    }

    private _getHourlyEndDate(): Date | null {
        const now = this._now();
        const startDate = new Date(0);
        startDate.setUTCFullYear(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
        startDate.setUTCHours(this._endHour.hours, this._endHour.minutes, this._endHour.seconds, 0);
        return startDate;
    }

    private _jackpotWillDropToday(): boolean {
        if (Check.isNullOrUndefined(this.lastWin)) {
            return true;
        }

        const lastWin = new Date(this.lastWin);
        const now = this._now();
        if (Number(lastWin) > Number(now)) {
            // last win is in the future?
            return false;
        }


        if (lastWin.getUTCDate() === now.getUTCDate() &&
            lastWin.getUTCMonth() === now.getUTCMonth() &&
            lastWin.getUTCFullYear() === now.getUTCFullYear()
        ) {
            return false;
        }

        const todayMustHit = this._getTodayMustHitDate();
        const delta = Number(todayMustHit) - Number(now);
        if (delta < 0) {
            return false;
        }

        return true;
    }

    get joinStatus(): PartyJackpotJoinStatus {
        return this._serviceFactory.blueRibbonService().getPartyJackpotJoinStatus(this.jackpotId);
    }

    protected _updateIsOptedIn(value: boolean): void {
        const originalIsOptedIn = this.isOptedIn;
        super._updateIsOptedIn(value);
        if(!value){
            if (originalIsOptedIn && this.isActive) {
                let analyticsData: IAnalyticsData = {
                    category: "Party_Jackpot",
                    action: "InGame_Widget_Hide",
                    label: this.name
                };
                this._serviceFactory.analyticsService().sendEvent(analyticsData);
            }
        }
    }

    protected _updateIsActive(value: boolean, inactiveReason: BLUE_RIBBON_INACTIVE_REASONS): void {
        const originalIsActive = this.isActive;
        super._updateIsActive(value, inactiveReason);
        if(!value && this.type === PartyJackpotType.Daily) {
            this._serviceFactory.blueRibbonService().makePartyJackpotStatusNeutral(this.jackpotId);
            if (originalIsActive && this.isOptedIn) {
                let analyticsData: IAnalyticsData = {
                    category: "Party_Jackpot",
                    action: "InGame_Widget_Hide_PartyEnded",
                    label: this.name
                };
                this._serviceFactory.analyticsService().sendEvent(analyticsData);
            }
        }
    }

    private _isInOffHours() {
        return (this.type === PartyJackpotType.Hourly && this.inactiveReason === "ACTIVE_TIME" && !this._isActive);
    }

    // we consider an hourly JP still active if inactiveReason is ACTIVE_TIME
    get isExpired() {
        const isInOffHours = this._isInOffHours();
        const isActive = this._isActive;
        return !(isInOffHours || isActive);
    }

    private _name: string = "";

    get name() {
        return this._name;
    }

    setName(name: string) {
        this._name = name;
    }
}
